import * as THREE from 'three';
import { clone as cloneSkinned } from 'three/examples/jsm/utils/SkeletonUtils.js';
import type { CharacterPackCache } from './character-pack-cache';
import { WeaponClass, RigSize } from '../config/character-roster';
import {
  WEAPON_TIMING, PARRY_WINDOW_MS, PARRY_RECOVERY_MS, SIDESTEP_DURATION_MS,
  PLAYBACK_RATE_MIN, PLAYBACK_RATE_MAX, CROSSFADE_SECONDS, IMPACT_VFX_DURATION_MS,
  ATTACK_ANTICIPATION_MULT, ATTACK_STRIKE_MULT, ATTACK_RECOVERY_MULT, HIT_FLINCH_WEIGHT, HIT_FLINCH_TIME_SCALE,
  MOVING_SPEED_EPSILON, BACKWARD_STEP_EPSILON, BACKWARD_DOT_THRESHOLD, LEVEL_UP_VFX,
} from '../config/character-visuals-tuning';
import { animGroupOf, clamp, AnimGroup } from './character-anim-groups';
import {
  clipCandidates, attackCandidates, resolveClip, STAGGER_CLIPS,
  type AnimIntent, type ClipStyle,
} from './animation-map';
import type { EntityStateName } from '../config/tuning';
import { HealthBarSprite } from './health-bar';
import { attachWornGear } from './worn-gear';

/**
 * The groups a jump's clip or a dodge's sidestep may play over (2026-10-16):
 * standing, walking and guarding. Mid-swing, parry, cast, stagger and death
 * keep their own clip — the jump's height still shows, the hop motion does not.
 */
const JUMP_ANIMATES_FROM: ReadonlySet<AnimGroup | null> = new Set<AnimGroup | null>([
  'IDLE', 'MOVING', 'SPRINTING', 'CROUCHING', 'BLOCKING',
]);

/**
 * design/gdd/character-visuals.md — a single player *or monster*'s rendered
 * avatar: model, retargeted animation, and attached weapon/shield. Generic
 * over `VisualEntry` + a `CharacterPackCache` since Skeleton AI's Bone
 * Warden amendment (2026-08-20) needed identical rendering for monsters —
 * same animation-state machine (`EntityStateName`, `MonsterState` mirrors
 * `PlayerState` exactly per skeleton-ai.md), same handslot-attachment
 * convention, just a different roster/cache (character-roster.ts +
 * adventurers-cache.ts for players, monster-roster.ts + skeletons-cache.ts
 * for monsters). One instance per entity (Core Rule #10 for players); the
 * underlying loaded geometry/clips are cached and shared per model name by
 * whichever `CharacterPackCache` is passed in, only the skeleton clone +
 * mixer are per-instance.
 */

export interface VisualEntry {
  weaponClass: WeaponClass;
  characterModel: string;
  rig: RigSize;
  weaponModel: string;
  shieldModel?: string;
  /** design/gdd/cosmetics-store.md's render hook — a recolored variant of the character's base texture atlas. Empty/undefined renders the default look. */
  skinAssetKey?: string;
  /**
   * design/gdd/equipment.md's worn-gear amendment — the gear def ids this
   * delver is wearing, in any order. `worn-gear.ts` reads each one's slot from
   * `GEAR_LABELS` and hangs it on the bone that owns it.
   *
   * Absent for a monster, a bot, and every character that predates the ladder.
   */
  wornGear?: readonly string[];
  /**
   * Which clip family this body animates with. Defaults to 'humanoid';
   * skeletal monsters pass 'undead' to get the pack's own Skeletons_* idle,
   * walk, death and awaken instead of a person's.
   */
  clipStyle?: ClipStyle;
}

/**
 * User report: "I do not like the combat dynamics, mechanics, or animations.
 * I do not feel the combat at all."
 *
 * Root cause at the time, verified by dumping every clip in the shipped rigs:
 * the two libraries the game had contained **26 animations and not one
 * attack** — locomotion, Hit/Death reactions, and generic gestures
 * (Interact/PickUp/Throw/Use_Item). No swing, no thrust, no guard. So the
 * knockback/hit-stop/shake/audio/speed-curve work from the Game Feel Polish
 * phases was all decorating a swing that wasn't there, and the stopgap was to
 * hand-author procedural additive swings against the rig's skeleton
 * (`combat-animations.ts`).
 *
 * **Resolved properly on 2026-08-25** by importing KayKit's Character
 * Animations pack, which is built on these exact rigs and ships the missing
 * motion: 1H and 2H attacks per direction, a block raise and guard loop, four
 * real dodges, a crouch, a sneak, and a spellcasting channel. The procedural
 * module is deleted, and every clip choice now lives in `animation-map.ts`,
 * which resolves per rig with fallbacks — the two rigs are not symmetric and
 * do not even agree on how to spell `Dodge_Backward`.
 *
 * A swing is a **full-body clip** now rather than an additive overlay. The
 * overlay existed because a procedural clip keyed only 5 of 23 bones and would
 * have let the rest drift toward bind pose; with a real clip the honest
 * playback is replacement, and it is correct here because `canMove` excludes
 * every attack state — you are rooted while swinging, so there is no
 * locomotion underneath to preserve. Blocking is the exception and stays
 * additive: `canMove` *includes* BLOCKING, so a guard has to layer over a walk.
 */

/** Below this opacity a faded body stops laying down depth first — see `setCameraFade`. */
export const FADE_DEPTH_PREPASS_MIN_ALPHA = 0.35;

export class CharacterVisual {
  readonly root = new THREE.Group();
  private mixer: THREE.AnimationMixer | null = null;
  private readonly clips = new Map<string, THREE.AnimationClip>();
  private currentAction: THREE.AnimationAction | null = null;
  private currentGroup: AnimGroup | null = null;
  private impactVfx: { mesh: THREE.Mesh; material: THREE.MeshBasicMaterial; elapsedS: number } | null = null;
  /**
   * The level-up pillar. Two meshes rather than one, because the column and the
   * ground ring animate on different curves — the column rises and fades, the
   * ring expands outward and fades faster — and a single mesh could do neither
   * convincingly.
   */
  private levelUpVfx: {
    column: THREE.Mesh; columnMaterial: THREE.MeshBasicMaterial;
    ring: THREE.Mesh; ringMaterial: THREE.MeshBasicMaterial;
    elapsedS: number;
  } | null = null;
  private healthBar: HealthBarSprite | null = null;
  private cache: CharacterPackCache | null = null;
  private rig: RigSize | null = null;
  private clipStyle: ClipStyle = 'humanoid';
  /**
   * The held guard, layered additively over locomotion. The only combat pose
   * that still overlays rather than replaces, because BLOCKING is the only
   * combat state `canMove` permits movement in — you can walk while guarding,
   * so the legs have to keep their clip.
   */
  private blockOverlay: THREE.AnimationAction | null = null;
  private blockOverlayClip: THREE.AnimationClip | null = null;
  private combatOverlayState: EntityStateName | null = null;
  /**
   * Planar speed, derived from consecutive `setPose` calls.
   *
   * CROUCHING covers both standing still and creeping forward
   * (`resolveMovementState` returns it whenever crouch is held, input or not),
   * so the state alone cannot choose between `Crouching` and `Sneaking`. Rather
   * than widen `setState`'s signature at all six call sites, the visual works
   * it out from the transforms it is already given every frame.
   */
  private lastPose: { x: number; z: number } | null = null;
  private planarSpeed = 0;
  /**
   * Whether this body is travelling roughly opposite the way it is facing.
   *
   * Meaningful only since mouse-look (camera-and-controls.md, fifth amendment):
   * facing follows the camera now, so travel and facing are independent, and
   * "walking backwards" became a thing a body can actually do. Worked out from
   * the transforms this class is already handed every frame, for the same
   * reason the crouch/sneak split is — the alternative is widening `setState`
   * at every call site to carry a value the visual can measure itself.
   */
  private movingBackward = false;
  /**
   * What the current channel actually is, when it is not a spell.
   *
   * The server has one CASTING state for every channel, so a barbarian
   * drinking a Tallow Salve and a druid casting Mend arrive here identical —
   * and a barbarian performing `Ranged_Magic_Spellcasting` to swallow a potion
   * is visibly wrong. The room broadcasts `consumable.channel` at activation
   * and that sets this, so the pose matches the act for everyone watching, not
   * just for the player who pressed the key.
   */
  private channelIntent: AnimIntent = 'CASTING';
  /**
   * A one-shot in flight — a premium emote, or (2026-08-26) the motion an
   * ability press plays on its caster. Restored to locomotion when it
   * finishes, or replaced outright if real gameplay interrupts it.
   *
   * One slot for both on purpose: they are the same kind of thing (a clip
   * that plays over a state the body has not left), and two slots would let a
   * Cleave and a dance run at once, each cross-fading the other out.
   */
  private oneShotAction: THREE.AnimationAction | null = null;
  /** Base locomotion clip currently playing — lets an ATTACK/PARRY overlay start and end without restarting the loop underneath it. */
  private currentBaseClip: string | null = null;
  /** Game Feel Polish Phase 2b — tracks real-ms elapsed since the current ATTACK group started, driving `computeAttackTimeScale`. Null outside the ATTACK group. */
  private attackPhase: { windupMs: number; activeMs: number; recoveryMs: number; baseRate: number; elapsedMs: number } | null = null;
  /** Camera-proximity fade state — see `setCameraFade`. Null until this instance has ever needed to fade. */
  private fadeMaterials: THREE.Material[] | null = null;
  private fadeAlpha = 1;

  static async create(entry: VisualEntry, cache: CharacterPackCache): Promise<CharacterVisual> {
    const visual = new CharacterVisual();
    await visual.load(entry, cache);
    return visual;
  }

  private async load(entry: VisualEntry, cache: CharacterPackCache): Promise<void> {
    this.cache = cache;
    this.rig = entry.rig;
    this.clipStyle = entry.clipStyle ?? 'humanoid';
    const [characterScene, clips] = await Promise.all([
      cache.loadCharacterModel(entry.characterModel),
      cache.loadRigAnimations(entry.rig),
    ]);

    // Plain Object3D.clone(true) does NOT rebind a SkinnedMesh's skeleton to
    // the cloned bones (it keeps pointing at the shared cached original) —
    // every instance of the same class would fight over one skeleton.
    // SkeletonUtils.clone() is the three.js-documented fix: it walks the
    // graph and rebuilds skeleton/bone references onto the clone.
    const character = cloneSkinned(characterScene) as THREE.Group;
    this.root.add(character);

    if (entry.skinAssetKey) await this.applySkin(character, entry.skinAssetKey, cache);

    this.mixer = new THREE.AnimationMixer(character);
    for (const clip of clips) this.clips.set(clip.name, clip);
    await this.prepareBlockOverlay();

    // THREE.GLTFLoader assigns every node's `.name` via
    // PropertyBinding.sanitizeNodeName(), which strips `[].:/ ` (reserved for
    // animation track-binding syntax) — so the source glTF's "handslot.r"
    // bone comes out the other end as "handslotr" at runtime. Confirmed by
    // dumping the actual live node names in a real browser (character-visual
    // has no way to catch this from a JSON/type check alone): the raw glTF
    // JSON node name is NOT what's queryable via getObjectByName() after
    // GLTFLoader parses it. This was the reason weapons never rendered.
    const handslotR = character.getObjectByName('handslotr');
    const handslotL = character.getObjectByName('handslotl');

    const weaponModel = await cache.loadEquipmentModel(entry.weaponModel);
    handslotR?.add(weaponModel.clone(true));

    if (entry.shieldModel) {
      const shieldModel = await cache.loadEquipmentModel(entry.shieldModel);
      handslotL?.add(shieldModel.clone(true));
    }

    // Before `setState`, and that ordering is load-bearing: every piece is
    // authored against the rest pose, and `worn-gear.ts` cancels the bone
    // transforms it reads here. Play a clip first and it would cancel whatever
    // frame of the idle happened to be up, freezing the whole set at that
    // offset for the character's life.
    // The skin goes in too, so worn pieces take the body's palette (QA
    // 2026-10-10: a crimson knight in a grey helm). Gear is always attached
    // after `applySkin` and always from scratch, so the order a player bought
    // the skin and the gear in can never matter.
    if (entry.wornGear?.length) await attachWornGear(character, entry.wornGear, cache, entry.skinAssetKey);

    this.setState('IDLE', entry.weaponClass);
  }

  /**
   * design/gdd/cosmetics-store.md Core Rule #6 — texture/material only, never
   * geometry/scale/hitbox. The cached character scene (character-pack-cache.ts)
   * is shared across every instance of the same class, and SkeletonUtils.clone()
   * does not deep-clone materials — mutating `.map` directly on a shared
   * material would reskin every other player of that class too. Each unique
   * source material gets cloned exactly once per character instance (a
   * Map, not a fresh clone per mesh) so meshes that intentionally share one
   * material (e.g. body + hood) still share their per-instance clone.
   * Follows Edge Case #6's "equipment fails degrades gracefully" precedent —
   * a missing/broken skin texture logs a warning and leaves the default look,
   * it never sinks the character's render.
   */
  private async applySkin(character: THREE.Group, skinAssetKey: string, cache: CharacterPackCache): Promise<void> {
    let texture: THREE.Texture;
    try {
      texture = await cache.loadSkinTexture(skinAssetKey);
    } catch (err) {
      console.warn(`[character-visual] failed to load skin "${skinAssetKey}", using default look`, err);
      return;
    }
    const cloned = new Map<THREE.Material, THREE.Material>();
    character.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (!mesh.isMesh) return;
      const swap = (mat: THREE.Material): THREE.Material => {
        let clone = cloned.get(mat);
        if (!clone) {
          clone = mat.clone();
          (clone as THREE.MeshStandardMaterial).map = texture;
          clone.needsUpdate = true;
          cloned.set(mat, clone);
        }
        return clone;
      };
      mesh.material = Array.isArray(mesh.material) ? mesh.material.map(swap) : swap(mesh.material);
    });
  }

  setPose(x: number, y: number, z: number, facing: number): void {
    if (this.lastPose) {
      // Distance since the last frame, smoothed so a single interpolation
      // hitch cannot flip a crouching character between two clips. Not a true
      // m/s — only the "is this body moving at all" threshold is ever read.
      const dx = x - this.lastPose.x;
      const dz = z - this.lastPose.z;
      const step = Math.hypot(dx, dz);
      this.planarSpeed = this.planarSpeed * 0.7 + step * 0.3;
      // Travel projected onto the facing direction, as a fraction of the step.
      // Left alone below the epsilon so a body that stops keeps whichever gait
      // it stopped in rather than flickering on interpolation noise.
      if (step > BACKWARD_STEP_EPSILON) {
        const along = (dx * Math.sin(facing) + dz * Math.cos(facing)) / step;
        this.movingBackward = along < -BACKWARD_DOT_THRESHOLD;
      }
      this.lastPose.x = x;
      this.lastPose.z = z;
    } else {
      this.lastPose = { x, z };
    }
    this.root.position.set(x, y, z);
    this.root.rotation.y = facing;
  }

  /**
   * A dormant skeleton coming alive (`design/gdd/skeleton-ai.md`).
   *
   * Played from the aggro transition rather than from a state change, because
   * DORMANT -> AGGROED is not an `EntityStateName` — the monster stays IDLE
   * either side of it. The pack ships `Skeletons_Awaken_Standing` for exactly
   * this, and until now the moment a pile of bones noticed you had no
   * animation at all: it simply started walking.
   *
   * One-shot over whatever is playing, and any real state change crossfades
   * away from it — so a skeleton that is attacked mid-awakening reacts instead
   * of finishing its flourish.
   */
  playAwaken(): void {
    const name = this.clipFor('AWAKEN');
    if (!name) return;
    this.playOnceHeld(name);
  }

  /**
   * Plays a premium emote clip (`design/gdd/cosmetics-store.md`).
   *
   * The clips live in their own lazily-fetched file, so this is the first place
   * that file is ever requested — a player who owns no emote never downloads
   * 215KB of dances. Failure is swallowed to a warning: a cosmetic that cannot
   * load must not break the run it was played in.
   *
   * Full-body and interruptible by design. Any real state change crossfades
   * away from it through the normal `setState` path, so getting attacked
   * mid-dance stops the dance rather than layering a swing over it.
   */
  async playEmote(clipName: string): Promise<void> {
    if (!this.mixer || !this.cache || !this.rig) return;
    let clips: THREE.AnimationClip[];
    try {
      clips = await this.cache.loadEmoteClips(this.rig);
    } catch (err) {
      console.warn(`[character-visual] failed to load emote clips for rig "${this.rig}"`, err);
      return;
    }
    const clip = clips.find((c) => c.name === clipName);
    // `this.mixer` re-checked: the load is async and the entity may have been
    // disposed while it was in flight.
    if (!clip || !this.mixer) return;

    const action = this.mixer.clipAction(clip);
    action.setLoop(THREE.LoopOnce, 1);
    // Released rather than held: an emote ends and you go back to standing.
    action.clampWhenFinished = false;
    action.timeScale = 1;
    this.oneShotAction = action;
    this.crossFadeTo(action);
  }

  /**
   * Visualizing abilities (2026-08-26) — the motion an ability press plays on
   * whoever pressed it.
   *
   * Core clips, not the emote file: these are combat motions the body already
   * carries, so nothing is fetched and a squadmate's Cleave animates on the
   * same frame the message arrives. `playEmote` loads a separate file and is
   * async for exactly that reason.
   *
   * A one-shot over the current state rather than a state of its own, because
   * every ability but Mend is instant — the server never leaves the caster in
   * anything but IDLE or MOVING, so there is no state for the machine to
   * show. Before this, pressing Eviscerate moved the body not at all.
   *
   * Skipped while an emote is running is *not* a special case: whichever
   * one-shot started last wins, and pressing an ability during a dance should
   * cancel the dance.
   */
  playAbilityAnimation(intent: AnimIntent, durationS: number): void {
    if (!this.mixer || !this.rig) return;
    const name = this.clipFor(intent);
    if (!name) return;
    // Timed rather than held: the clip has to be out of the way by the time
    // the GCD is, or a 1.25s rotation turns into a queue of half-played
    // animations.
    this.playOnceTimed(name, durationS);
    this.oneShotAction = this.currentAction;
  }

  /**
   * Jump (2026-10-16) — the hop's clip, one-shot over locomotion and fitted to
   * the server's airtime. The height is not here: the caller lifts the root
   * through `setPose`'s y with `sim/jump.ts`'s arc. Skipped mid-swing, parry,
   * stagger or death (the jump never interrupts those; only the arc shows),
   * and on a rig whose only answer is the idle pose.
   */
  playJump(durationMs: number): void {
    if (!this.mixer || !this.rig) return;
    if (!JUMP_ANIMATES_FROM.has(this.currentGroup)) return;
    const name = this.clipFor('JUMP');
    if (!name || name === this.clipFor('IDLE')) return;
    this.playOnceTimed(name, durationMs / 1000);
    this.oneShotAction = this.currentAction;
  }

  /**
   * A passive dodge landing (2026-10-16): a short sidestep, left or right at
   * random, then back to whatever the state machine says. Nothing else moves —
   * the server never displaced the body, so neither does this.
   */
  playDodgeSidestep(): void {
    if (!this.mixer || !this.rig) return;
    if (!JUMP_ANIMATES_FROM.has(this.currentGroup)) return;
    const name = this.clipFor(Math.random() < 0.5 ? 'SIDESTEP_LEFT' : 'SIDESTEP_RIGHT');
    if (!name || name === this.clipFor('IDLE')) return;
    this.playOnceTimed(name, SIDESTEP_DURATION_MS / 1000);
    this.oneShotAction = this.currentAction;
  }

  /**
   * Marks the channel that is about to start as something other than a spell.
   *
   * Called just before the CASTING state arrives, from the room's
   * `consumable.channel` broadcast. Reset automatically when the channel ends.
   */
  setChannelIntent(intent: AnimIntent): void {
    this.channelIntent = intent;
  }

  /** Whether this body is moving, for the clip choices that split on it (crouch vs sneak). */
  private isMoving(): boolean {
    return this.planarSpeed > MOVING_SPEED_EPSILON;
  }

  /**
   * Which locomotion loop belongs to the group currently playing.
   *
   * Two of these choices depend on values that change *while* a group is held
   * rather than on entry — whether a crouching body is creeping, and (since
   * mouse-look) whether a walking body is walking backwards — which is why
   * `refreshLocomotion` re-asks every frame instead of `setState` deciding
   * once and being done. Anything that is not locomotion answers IDLE and is
   * never asked: `refreshLocomotion` only calls this for the groups below.
   */
  private locomotionIntent(): AnimIntent {
    switch (this.currentGroup) {
      case 'SPRINTING': return 'SPRINTING';
      case 'CROUCHING': return this.isMoving() ? 'CROUCH_MOVING' : 'CROUCH_IDLE';
      case 'MOVING': return this.movingBackward ? 'MOVING_BACKWARD' : 'MOVING';
      // The guard itself is the additive overlay; underneath it, blocking is
      // the one combat state you can walk (and back away) in.
      case 'BLOCKING': return this.isMoving() ? (this.movingBackward ? 'MOVING_BACKWARD' : 'MOVING') : 'IDLE';
      default: return 'IDLE';
    }
  }

  /**
   * Re-picks the locomotion loop for the group already playing.
   *
   * `setState` returns early when the group has not changed — correct, and the
   * reason an attack overlay doesn't restart a walk — but it means a change
   * *within* a group was previously invisible: a crouching player who started
   * creeping kept the standing crouch until some unrelated state change, and a
   * player who turned to back away would have kept a forward stride forever.
   * `playLoop` no-ops when the resolved clip is unchanged, so this costs a map
   * lookup on the frames where nothing has changed, which is nearly all of them.
   */
  private refreshLocomotion(): void {
    if (this.oneShotAction?.isRunning()) return;
    switch (this.currentGroup) {
      case 'MOVING': case 'SPRINTING': case 'CROUCHING': case 'BLOCKING':
        this.playIntentLoop(this.locomotionIntent());
        break;
      default: break;
    }
  }

  /**
   * The clip for an intent on this body's rig and style, or null when the rig
   * genuinely has nothing — see `animation-map.ts` on why a chain rather than
   * a name, and why null rather than a guess.
   */
  private clipFor(intent: AnimIntent): string | null {
    if (!this.rig) return null;
    return resolveClip(clipCandidates(this.rig, this.clipStyle, intent), new Set(this.clips.keys()));
  }

  /**
   * Camera-proximity fade — see `render/proximity-fade.ts` for why.
   *
   * Materials come from the shared `CharacterPackCache` and are NOT deep-
   * cloned by `SkeletonUtils.clone()` (the same trap `applySkin` documents),
   * so setting `.opacity` on them directly would fade every other character
   * of the same class along with this one. Cloned lazily, once, on the first
   * frame this instance actually needs to fade: most characters never come
   * near the camera, and cloning a material per instance up-front would
   * throw away the cache's whole point.
   *
   * `depthWrite` goes off while translucent — otherwise the faded model
   * still writes depth and punches a hole through everything drawn behind
   * it, which looks worse than the opaque body it replaced.
   *
   * **The depth pre-pass (2026-10-15).** QA: "from behind, the helmet looks
   * split into separate segments". With depth writes off, a translucent body
   * draws every one of its surfaces — the inside of the far side of the helm,
   * the face under it, the visor slit — in whatever order its triangles come,
   * so the nearest surface no longer wins. At an alpha of 0.9 the face showed
   * through the back of the helm; the self-fade starts as soon as the boom
   * shortens against a corridor wall, which is exactly where the tester stood.
   * So each opaque mesh gets a depth-only twin, drawn first in the transparent
   * pass: it lays down the body's front surface and nothing else, and the
   * colour pass after it can only land on that surface. One layer of glass,
   * not a stack of them. Drawn in the transparent pass rather than with the
   * opaque scene so the walls behind are already there — an opaque depth-only
   * mesh would sort first and stop the room behind it from being drawn at all.
   */
  setCameraFade(alpha: number): void {
    const clamped = Math.min(1, Math.max(0, alpha));
    // Cheap guard: this runs per character per frame, and the overwhelmingly
    // common case is "already fully opaque, still fully opaque".
    if (Math.abs(clamped - this.fadeAlpha) < 0.01) return;
    this.fadeAlpha = clamped;
    if (!this.fadeMaterials) this.fadeMaterials = this.cloneMaterialsForFade();
    const translucent = clamped < 0.999;
    for (const material of this.fadeMaterials) {
      material.transparent = translucent;
      material.opacity = clamped;
      material.depthWrite = !translucent;
    }
    // Only while the body is still mostly there. Below that its inner faces
    // are too faint to read as anything, and a twin of a nearly invisible
    // squadmate would still hide a torch flame or a spell drawn behind them.
    for (const twin of this.fadeDepthTwins) twin.visible = translucent && clamped >= FADE_DEPTH_PREPASS_MIN_ALPHA;
  }

  /** Depth-only copies of the body's meshes — see `setCameraFade`. */
  private fadeDepthTwins: THREE.Mesh[] = [];

  /** Clones each unique material once per instance (a Map, so meshes that intentionally share one keep sharing this instance's clone) — same approach as `applySkin`. */
  private cloneMaterialsForFade(): THREE.Material[] {
    const cloned = new Map<THREE.Material, THREE.Material>();
    const meshes: THREE.Mesh[] = [];
    this.root.traverse((obj) => { if ((obj as THREE.Mesh).isMesh) meshes.push(obj as THREE.Mesh); });
    // Opaque meshes only get a twin: an effect that is already translucent
    // (the level-up column, an impact flash) never needed one.
    const opaque = meshes.filter((mesh) => (Array.isArray(mesh.material) ? mesh.material : [mesh.material])
      .every((m) => !m.transparent));
    for (const mesh of meshes) {
      const swap = (mat: THREE.Material): THREE.Material => {
        let clone = cloned.get(mat);
        if (!clone) { clone = mat.clone(); cloned.set(mat, clone); }
        return clone;
      };
      mesh.material = Array.isArray(mesh.material) ? mesh.material.map(swap) : swap(mesh.material);
    }
    this.fadeDepthTwins = opaque.map((mesh) => this.depthTwinOf(mesh));
    return [...cloned.values()];
  }

  /**
   * A depth-only copy of `mesh`, parented to it so it inherits its transform
   * and its visibility (a helmet hidden by a worn helm stays hidden).
   *
   * A skinned twin binds to the same skeleton, so it poses with the body
   * frame for frame. `renderOrder` -1 against the body's 0 puts it ahead in
   * the transparent pass whatever the distance sort says.
   */
  private depthTwinOf(mesh: THREE.Mesh): THREE.Mesh {
    const material = new THREE.MeshBasicMaterial({ colorWrite: false, side: THREE.DoubleSide });
    material.transparent = true;
    let twin: THREE.Mesh;
    const skinned = mesh as THREE.SkinnedMesh;
    if (skinned.isSkinnedMesh) {
      const skinnedTwin = new THREE.SkinnedMesh(mesh.geometry, material);
      skinnedTwin.bindMode = skinned.bindMode;
      skinnedTwin.bind(skinned.skeleton, skinned.bindMatrix);
      twin = skinnedTwin;
    } else {
      twin = new THREE.Mesh(mesh.geometry, material);
    }
    twin.name = `${mesh.name}__fadeDepth`;
    twin.renderOrder = mesh.renderOrder - 1;
    twin.frustumCulled = mesh.frustumCulled;
    twin.visible = false;
    mesh.add(twin);
    return twin;
  }

  /**
   * User-requested: "HP bars above the mobs... not clear how quickly I can
   * kill them." Monster-only — attached explicitly by the caller
   * (dungeon-instance-bootstrap.ts), never by `load()`, so players never
   * get one. Parented to `root` at a pure Y offset (0, heightOffset, 0):
   * rotating `root` around its own Y axis never moves a point that's
   * already on that axis, so the bar stays fixed above the model
   * regardless of which way the character is facing, with no
   * counter-rotation needed.
   */
  attachHealthBar(heightOffset: number): void {
    if (this.healthBar) return;
    this.healthBar = new HealthBarSprite();
    this.healthBar.group.position.y = heightOffset;
    this.root.add(this.healthBar.group);
  }

  setHealthFraction(fraction01: number): void {
    this.healthFraction = fraction01;
    this.healthBar?.setFraction(fraction01);
    if (this.healthBar && !this.tellsVisible) this.healthBar.group.visible = false;
  }

  /** Last HP fed to the bar; a corpse (0) never gets its empty bar back. */
  private healthFraction = 1;
  private tellsVisible = true;

  /**
   * Whether this body's tells — rim light and HP bar — are drawn (2026-10-16).
   * Both ignore the scene's lighting (the rim is additive, the bar ignores
   * depth), so on a monster streamed beyond what lighting.md lets a player see
   * they would label an unlit skeleton through the dark. The caller decides
   * from the light state; see `render/monster-reveal.ts`. The lantern light
   * that used to live here moved to the scene's fixed pool (QA #9).
   */
  setTellsVisible(visible: boolean): void {
    this.tellsVisible = visible;
    // Called every frame for anything in reveal range, corpses included.
    if (this.healthBar) this.healthBar.group.visible = visible && this.healthFraction > 0;
    if (this.rimIntensityUniform) this.rimIntensityUniform.value = visible ? this.rimIntensity : 0;
    // The skeletons' eye sockets are an emissive "Glow" material, which also
    // feeds bloom — a pair of lit eyes 16m down a dark corridor is a tell just
    // like the rim (2026-10-16). Only the per-instance clones `applyRimLight`
    // made are touched, never a material the asset cache shares.
    for (const g of this.glowMaterials()) g.material.emissiveIntensity = visible ? g.base : 0;
  }

  private glowCache: Array<{ material: THREE.MeshStandardMaterial; base: number }> | null = null;

  private glowMaterials(): Array<{ material: THREE.MeshStandardMaterial; base: number }> {
    if (this.glowCache) return this.glowCache;
    const found = new Map<THREE.MeshStandardMaterial, number>();
    this.root.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (!mesh.isMesh) return;
      for (const m of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) {
        const std = m as THREE.MeshStandardMaterial;
        if (!std.userData?.[CharacterVisual.RIM_LIGHT_FLAG] || !std.emissive) continue;
        if (std.emissive.getHex() === 0 && !std.emissiveMap) continue;
        if (!found.has(std)) found.set(std, std.emissiveIntensity);
      }
    });
    // Before the rim is applied there is nothing safe to touch; ask again later.
    if (!this.rimIntensityUniform) return [];
    this.glowCache = [...found].map(([material, base]) => ({ material, base }));
    return this.glowCache;
  }

  update(dtSeconds: number): void {
    this.mixer?.update(dtSeconds);
    // Phase 2b's per-phase speed curve drives the swing itself, which is the
    // base action again now that the swing is a real full-body clip.
    if (this.attackPhase && this.currentAction) {
      this.attackPhase.elapsedMs += dtSeconds * 1000;
      this.currentAction.timeScale = this.computeAttackTimeScale(this.attackPhase);
    }
    // An emote that ran to its end returns the body to locomotion. Not driven
    // by a timer: the clip's own length decides, and a real state change in the
    // meantime will have crossfaded away from it already.
    if (this.oneShotAction && !this.oneShotAction.isRunning()) {
      this.oneShotAction = null;
      this.currentBaseClip = null; // force the loop to restart rather than no-op
      this.playIntentLoop(this.isMoving() ? this.locomotionIntent() : 'IDLE');
    }
    // A change within the group already playing — see refreshLocomotion.
    this.refreshLocomotion();
    this.updateImpactVfx(dtSeconds);
    this.updateLevelUpVfx(dtSeconds);
  }

  /**
   * Game Feel Polish Phase 2b — Formula 1's total swing duration is a
   * contract other systems (server timing, hit resolution) already rely on,
   * so per-phase speed can't just multiply the base rate directly or the
   * clip would finish early/late. Normalizing by the phase-weighted average
   * multiplier keeps the *time-weighted* average scale at ~1x baseRate,
   * so total clip traversal over the full swing stays close to
   * `clip.duration` even though windup/strike/recovery each run at a
   * different speed.
   */
  private computeAttackTimeScale(phase: { windupMs: number; activeMs: number; recoveryMs: number; baseRate: number; elapsedMs: number }): number {
    const { windupMs, activeMs, recoveryMs, baseRate, elapsedMs } = phase;
    const totalMs = windupMs + activeMs + recoveryMs;
    const avgMult = (ATTACK_ANTICIPATION_MULT * windupMs + ATTACK_STRIKE_MULT * activeMs + ATTACK_RECOVERY_MULT * recoveryMs) / totalMs;
    const phaseMult = elapsedMs < windupMs
      ? ATTACK_ANTICIPATION_MULT
      : elapsedMs < windupMs + activeMs
        ? ATTACK_STRIKE_MULT
        : ATTACK_RECOVERY_MULT;
    return clamp(baseRate * (phaseMult / avgMult), PLAYBACK_RATE_MIN, PLAYBACK_RATE_MAX);
  }

  /**
   * Combat-feel amendment (2026-08-21) — fired on every landed hit's
   * defender (dungeon-instance-bootstrap.ts's `combat.result` handler), for
   * players and monsters alike, regardless of whether the hit staggers
   * (most don't — Poise & Stagger's strict `>` comparison means a same-tier
   * or Light-vs-anything-but-Cloth matchup shows zero reaction otherwise).
   * A small, fast white burst at chest height — separate slot from
   * `levelUpVfx` so a celebration and a landed hit in the same instant
   * don't fight over one mesh. (Ability effects moved out of this class
   * entirely in 2026-08-26 — see `render/ability-fx.ts`.)
   */
  playImpactVfx(): void {
    if (this.impactVfx) this.disposeImpactVfx();
    const geometry = new THREE.SphereGeometry(0.18, 8, 8);
    const material = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 1 });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.position.y = 1.2;
    this.root.add(mesh);
    this.impactVfx = { mesh, material, elapsedS: 0 };
  }

  private updateImpactVfx(dtSeconds: number): void {
    if (!this.impactVfx) return;
    this.impactVfx.elapsedS += dtSeconds;
    const t = clamp(this.impactVfx.elapsedS / (IMPACT_VFX_DURATION_MS / 1000), 0, 1);
    const scale = 1 + t * 1.5;
    this.impactVfx.mesh.scale.set(scale, scale, scale);
    this.impactVfx.material.opacity = 1 - t;
    if (t >= 1) this.disposeImpactVfx();
  }

  /**
   * A column of light on this body — the level-up celebration
   * (`LEVEL_UP_VFX`), fired from the room's `character.levelup` broadcast.
   *
   * Parented to `root` like the other two VFX, so it travels with the
   * character: WoW's own does, and a pillar left behind at the spot where the
   * kill happened would read as a world event rather than as something that
   * happened to *you*. Additive-blended with `depthWrite: false` so it glows
   * rather than z-fighting the model it surrounds.
   *
   * **`BackSide`, and that is the whole difference between a celebration and a
   * white-out.** The first live capture rendered this double-sided, so the
   * cylinder's near wall drew additively *over* the character and washed the
   * body to near-white — the thing being celebrated became the least legible
   * object in the frame. Culling the near face puts the glow behind and around
   * the figure instead, keeping its silhouette crisp, and still works when the
   * boom collapses and the camera ends up inside the column: from in there,
   * the far wall is the one you see.
   *
   * Restarting replaces the effect outright rather than layering a second one —
   * two levels in quick succession is one celebration, not two overlapping
   * glows at double brightness.
   */
  playLevelUpVfx(): void {
    if (this.levelUpVfx) this.disposeLevelUpVfx();
    const columnMaterial = new THREE.MeshBasicMaterial({
      color: LEVEL_UP_VFX.COLOR, transparent: true, opacity: 0,
      side: THREE.BackSide, depthWrite: false, blending: THREE.AdditiveBlending,
    });
    // Open-ended: a capped cylinder would put a bright disc over the
    // character's head, which from the shoulder camera is most of the frame.
    const column = new THREE.Mesh(
      new THREE.CylinderGeometry(LEVEL_UP_VFX.COLUMN_RADIUS_M, LEVEL_UP_VFX.COLUMN_RADIUS_M, LEVEL_UP_VFX.COLUMN_HEIGHT_M, 20, 1, true),
      columnMaterial,
    );
    column.position.y = LEVEL_UP_VFX.COLUMN_HEIGHT_M / 2;

    const ringMaterial = new THREE.MeshBasicMaterial({
      color: LEVEL_UP_VFX.COLOR, transparent: true, opacity: 0,
      side: THREE.DoubleSide, depthWrite: false, blending: THREE.AdditiveBlending,
    });
    const ring = new THREE.Mesh(new THREE.RingGeometry(LEVEL_UP_VFX.RING_INNER_M, LEVEL_UP_VFX.RING_OUTER_M, 28), ringMaterial);
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.06; // just clear of the floor tile's own top surface

    this.root.add(column, ring);
    this.levelUpVfx = { column, columnMaterial, ring, ringMaterial, elapsedS: 0 };
  }

  /**
   * The column snaps to full brightness and fades over the rest of its life;
   * the ring expands and fades in the first two thirds.
   *
   * The fast attack matters: a symmetric fade in and out would read as a soft
   * glow drifting past, where the thing being sold is a moment of arrival.
   */
  private updateLevelUpVfx(dtSeconds: number): void {
    if (!this.levelUpVfx) return;
    const v = this.levelUpVfx;
    v.elapsedS += dtSeconds;
    const t = clamp(v.elapsedS / (LEVEL_UP_VFX.DURATION_MS / 1000), 0, 1);

    // Up in the first 12% of the effect, then a long ease out.
    const columnFade = t < 0.12 ? t / 0.12 : 1 - (t - 0.12) / 0.88;
    /**
     * Scaled by the same camera-proximity fade the body itself uses.
     *
     * Found in the second live capture: when the boom collapses against a wall
     * the eye ends up *inside* the column, and the far wall then fills half the
     * frame with flat gold. The body already fades out at that distance
     * (`proximity-fade.ts`), so its aura fading with it is the consistent
     * answer rather than a second special case — and it is exactly the moment
     * the pillar has nothing left to frame.
     */
    v.columnMaterial.opacity = LEVEL_UP_VFX.COLUMN_PEAK_OPACITY * Math.max(0, columnFade) * this.fadeAlpha;
    const rise = 1 + t * (LEVEL_UP_VFX.COLUMN_RISE_SCALE - 1);
    v.column.scale.y = rise;
    v.column.position.y = (LEVEL_UP_VFX.COLUMN_HEIGHT_M * rise) / 2;

    const ringT = clamp(t / 0.66, 0, 1);
    const ringScale = 1 + ringT * (LEVEL_UP_VFX.RING_EXPAND_SCALE - 1);
    v.ring.scale.set(ringScale, ringScale, ringScale);
    v.ringMaterial.opacity = LEVEL_UP_VFX.RING_PEAK_OPACITY * (1 - ringT) * this.fadeAlpha;

    if (t >= 1) this.disposeLevelUpVfx();
  }

  private disposeLevelUpVfx(): void {
    if (!this.levelUpVfx) return;
    this.root.remove(this.levelUpVfx.column, this.levelUpVfx.ring);
    this.levelUpVfx.column.geometry.dispose();
    this.levelUpVfx.columnMaterial.dispose();
    this.levelUpVfx.ring.geometry.dispose();
    this.levelUpVfx.ringMaterial.dispose();
    this.levelUpVfx = null;
  }

  private disposeImpactVfx(): void {
    if (!this.impactVfx) return;
    this.root.remove(this.impactVfx.mesh);
    this.impactVfx.mesh.geometry.dispose();
    this.impactVfx.material.dispose();
    this.impactVfx = null;
  }

  /** Guards against re-patching an already-patched material if `applyRimLight` is somehow called more than once on the same instance (e.g. a future caller re-adding it defensively). */
  private static readonly RIM_LIGHT_FLAG = '__tallowRimLightApplied';

  /**
   * Game Feel Polish Phase 4 — a Fresnel-style rim highlight via
   * `onBeforeCompile`, not a real dynamic light: cheaper (a per-pixel shader
   * term, not another forward light the renderer has to loop every fragment
   * against) and scales to any monster count without per-instance light
   * budget concerns. Injects its own varyings computed fresh in the vertex
   * shader rather than depending on any of MeshStandardMaterial's own
   * internal varyings (e.g. `vViewPosition`), which are only conditionally
   * declared depending on which features (fog/envmap/clearcoat) are active
   * — self-contained is safer than assuming one of those happens to be on.
   *
   * Per instance (2026-10-16). SkeletonUtils.clone() (see `load()`'s own
   * comment) does NOT deep-clone materials, and this used to patch the
   * archetype's shared material — fine while every skeleton showed its rim
   * all the time. Now the rim is a *tell* that only shows on a body the
   * player could actually see (`setTellsVisible`, lighting.md Core Rule 6),
   * so each instance gets its own material clone and its own intensity
   * uniform. The shader source is identical across instances, so they all
   * still share one compiled program.
   */
  applyRimLight(colorHex: number, power: number, intensity: number): void {
    if (this.rimIntensityUniform) return;
    const intensityUniform = { value: intensity };
    this.rimIntensityUniform = intensityUniform;
    this.rimIntensity = intensity;
    const clones = new Map<THREE.Material, THREE.Material>();
    const swap = (source: THREE.Material): THREE.Material => {
      let clone = clones.get(source);
      if (clone) return clone;
      clone = source.clone();
      clones.set(source, clone);
      (clone as THREE.Material & { userData: Record<string, unknown> }).userData[CharacterVisual.RIM_LIGHT_FLAG] = true;
      clone.onBeforeCompile = (shader) => {
        shader.uniforms.rimColor = { value: new THREE.Color(colorHex) };
        shader.uniforms.rimPower = { value: power };
        shader.uniforms.rimIntensity = intensityUniform;
        shader.vertexShader = shader.vertexShader
          .replace('#include <common>', '#include <common>\nvarying vec3 vRimNormal;\nvarying vec3 vRimViewDir;')
          .replace(
            '#include <begin_vertex>',
            '#include <begin_vertex>\nvRimNormal = normalize(normalMatrix * normal);\nvRimViewDir = normalize(-(modelViewMatrix * vec4(position, 1.0)).xyz);',
          );
        shader.fragmentShader = shader.fragmentShader
          .replace(
            '#include <common>',
            '#include <common>\nuniform vec3 rimColor;\nuniform float rimPower;\nuniform float rimIntensity;\nvarying vec3 vRimNormal;\nvarying vec3 vRimViewDir;',
          )
          .replace(
            '#include <dithering_fragment>',
            'float rimFactor = pow(1.0 - max(dot(normalize(vRimNormal), normalize(vRimViewDir)), 0.0), rimPower);\ngl_FragColor.rgb += rimColor * rimFactor * rimIntensity;\n#include <dithering_fragment>',
          );
      };
      return clone;
    };
    this.root.traverse((obj) => {
      const mesh = obj as THREE.Mesh;
      if (!mesh.isMesh) return;
      mesh.material = Array.isArray(mesh.material) ? mesh.material.map(swap) : swap(mesh.material);
    });
  }

  /** This instance's rim intensity, shared by its cloned materials' shaders — see `applyRimLight`. */
  private rimIntensityUniform: { value: number } | null = null;
  private rimIntensity = 0;

  /**
   * Game Feel Polish Phase 2c — additive upper-body flinch for a landed hit
   * that does NOT trigger a full STAGGERED/DEAD replace-animation (the
   * common case per Poise & Stagger). Reuses the rig's own Hit_A/Hit_B
   * clips via `CharacterPackCache.loadFlinchClip`'s additive-converted,
   * per-rig-cached copies — never the original clips `playOnceHeld` uses
   * for a real stagger, since converting a clip to additive mutates it.
   * Fire-and-forget: a hit landing mid-load (still awaiting the flinch
   * clip) or after `dispose()` just silently skips the flinch, same
   * degrade-gracefully precedent as `applySkin`.
   */
  async playHitFlinch(): Promise<void> {
    if (!this.mixer || !this.cache || !this.rig) return;
    const clipName = Math.random() < 0.5 ? 'Hit_A' : 'Hit_B';
    let clip: THREE.AnimationClip;
    try {
      clip = await this.cache.loadAdditiveClip(this.rig, clipName);
    } catch (err) {
      console.warn(`[character-visual] failed to load flinch clip "${clipName}"`, err);
      return;
    }
    if (!this.mixer) return; // disposed while awaiting the clip
    const action = this.mixer.clipAction(clip);
    action.blendMode = THREE.AdditiveAnimationBlendMode;
    action.setLoop(THREE.LoopOnce, 1);
    action.clampWhenFinished = false; // deactivates cleanly once the one-shot finishes, instead of holding the last additive offset forever
    action.timeScale = HIT_FLINCH_TIME_SCALE;
    action.weight = HIT_FLINCH_WEIGHT;
    action.reset().play();
  }

  /**
   * Core Rule #6 / Formulas 1-4. No-op if the state hasn't left its current
   * animation group (e.g. ATTACK_WINDUP -> ATTACK_ACTIVE).
   *
   * `attackDirection` (melee-combat.md's LEFT/OVERHEAD/RIGHT) selects which
   * swing plays; omitted falls back to the overhead chop, the LMB default.
   */
  setState(state: EntityStateName, weaponClass: WeaponClass, attackDirection?: string): void {
    if (!this.mixer) return;
    const group = animGroupOf(state);
    // Driven by the exact state, not the collapsed group: BLOCKING collapses
    // into 'IDLE' (character-anim-groups.ts), so the guard stance would be
    // invisible to a group-based check.
    this.updateCombatOverlay(state);
    if (group === this.currentGroup) return;
    this.currentGroup = group;
    // A real state change owns the body from here (2026-10-16). Without this a
    // jump or ability one-shot that the change crossfaded away would, once its
    // fade ended, read as "finished" in `update` and restart locomotion over
    // the swing that replaced it — and a swing mid-jump is allowed.
    this.oneShotAction = null;

    // Edge Case: DISCONNECTED pauses the mixer; anything else resumes it —
    // covers reconnect (Movement's Edge Case #5) with no special-casing here.
    this.mixer.timeScale = group === 'DISCONNECTED' ? 0 : 1;

    // A channel intent is scoped to one channel. Cleared on the way out so the
    // next cast is a cast again, whatever the last one was.
    if (group !== 'CASTING') this.channelIntent = 'CASTING';

    // The Phase 2b speed curve belongs to the swing and nothing else. It used
    // to be cleared by `stopCombatOverlay`; with the swing promoted to the base
    // clip, leaving it armed would keep modulating `currentAction.timeScale`
    // after the swing — i.e. speed up the idle or walk that followed it.
    if (group !== 'ATTACK') this.attackPhase = null;

    switch (group) {
      case 'IDLE': this.playIntentLoop('IDLE'); break;
      // All four locomotion groups resolve through one place, because two of
      // them split on something that keeps changing while the group is held
      // (creeping vs standing crouch, forwards vs backwards walk) and
      // `refreshLocomotion` has to be able to re-ask the same question.
      case 'MOVING': case 'SPRINTING': case 'CROUCHING': case 'BLOCKING':
        this.playIntentLoop(this.locomotionIntent());
        break;
      // A channel loops for as long as it lasts — the server ends it, not the
      // clip, so a one-shot would finish early and drop back to idle mid-cast.
      case 'CASTING': this.playIntentLoop(this.channelIntent); break;
      case 'ATTACK': this.playAttack(weaponClass, attackDirection); break;
      case 'PARRY':
        this.playIntentOnceTimed('PARRY', (PARRY_WINDOW_MS + PARRY_RECOVERY_MS) / 1000); // Formula 2
        break;
      case 'STAGGERED': {
        // Formula 4 — alternate the two hit reactions so a flurry doesn't
        // replay one pose. The large rig only has Hit_A.
        const options = this.rig ? STAGGER_CLIPS[this.rig] : ['Hit_A'];
        this.playOnceHeld(options[Math.floor(Math.random() * options.length)]);
        break;
      }
      case 'DEAD': this.playIntentOnceHeld('DEAD'); break;
      case 'DISCONNECTED': break; // mixer already paused above; leave whatever pose is showing
    }
  }

  /**
   * A real swing, fitted to the weapon's own total time.
   *
   * Full-body replacement rather than the additive overlay this used to be —
   * see the note at the top of the file. The clip is time-scaled so it spans
   * exactly `melee-combat.md` Formula 1's windup + active + recovery, which is
   * what keeps the visible swing and the server's hit resolution talking about
   * the same moment.
   *
   * The one imperfect fit is LIGHT + RIGHT: `Melee_1H_Attack_Slice_Horizontal`
   * is 1.367s against a 420ms budget, needing 3.25x where `PLAYBACK_RATE_MAX`
   * caps at 3.0 — so that swing's tail runs ~36ms past its state, and gets
   * cross-faded out. That is what the Edge Case #1 clamp is for, and it is the
   * only one of the nine weapon/direction combinations that reaches it.
   */
  private playAttack(weaponClass: WeaponClass, attackDirection?: string): void {
    if (!this.rig) return;
    const name = resolveClip(
      attackCandidates(this.rig, weaponClass, attackDirection), new Set(this.clips.keys()),
    );
    if (!name) return;
    const t = WEAPON_TIMING[weaponClass];
    const totalS = (t.windupMs + t.activeMs + t.recoveryMs) / 1000;
    this.playOnceTimed(name, totalS);
    // Phase 2b — anticipation slow, strike fast, recovery medium. It modulates
    // the base action now that the swing IS the base action.
    const clip = this.clips.get(name);
    if (clip) {
      const baseRate = clamp(clip.duration / totalS, PLAYBACK_RATE_MIN, PLAYBACK_RATE_MAX);
      this.attackPhase = { ...t, baseRate, elapsedMs: 0 };
    }
  }

  private playIntentLoop(intent: AnimIntent): void {
    const name = this.clipFor(intent);
    if (name) this.playLoop(name);
  }

  private playIntentOnceTimed(intent: AnimIntent, targetDurationS: number): void {
    const name = this.clipFor(intent);
    if (name) this.playOnceTimed(name, targetDurationS);
  }

  private playIntentOnceHeld(intent: AnimIntent): void {
    const name = this.clipFor(intent);
    if (name) this.playOnceHeld(name);
  }

  /**
   * Builds the additive guard clip once, at load, against **`Idle_A` as the
   * reference pose** rather than against itself.
   *
   * This is the whole trick, and getting it wrong makes the guard invisible.
   * `makeClipAdditive(clip)` subtracts the clip's *own* first frame — which
   * for a guard *loop* like `Melee_Blocking` already IS the guard stance, so
   * the delta would be nothing but the idle sway within the loop. Passing
   * `Idle_A` as the reference makes the delta "guard minus standing", which is
   * the pose difference that actually needs layering over a walk.
   *
   * (The hit-flinch in `loadFlinchClip` correctly uses its own first frame:
   * frame 0 of `Hit_A` is the neutral pre-impact pose, so the delta there is
   * the flinch itself.)
   */
  private async prepareBlockOverlay(): Promise<void> {
    if (!this.cache || !this.rig) return;
    const guard = this.clipFor('BLOCKING');
    const reference = this.clipFor('IDLE');
    // Nothing to layer if the rig's guard resolved to the idle pose itself —
    // the delta would be zero and the action pure overhead.
    if (!guard || !reference || guard === reference) return;
    try {
      this.blockOverlayClip = await this.cache.loadAdditiveClip(this.rig, guard, reference);
    } catch (err) {
      console.warn(`[character-visual] failed to build additive guard from "${guard}"`, err);
    }
  }

  /**
   * Starts/stops the additive guard overlay from the exact entity state.
   *
   * Only BLOCKING overlays now. Attack and parry became real full-body clips
   * with the animation-pack import — they are rooted states, so replacing the
   * base clip is both simpler and more honest than layering a partial pose.
   */
  private updateCombatOverlay(state: EntityStateName): void {
    if (!this.mixer) return;
    if (state === this.combatOverlayState) return;
    this.combatOverlayState = state;

    if (state !== 'BLOCKING') {
      this.stopBlockOverlay();
      return;
    }
    if (!this.blockOverlayClip || this.blockOverlay) return;

    const action = this.mixer.clipAction(this.blockOverlayClip);
    action.blendMode = THREE.AdditiveAnimationBlendMode;
    // Looped and held for as long as the button is: block is a stance you
    // stand in, not a motion that completes.
    action.setLoop(THREE.LoopRepeat, Infinity);
    action.weight = 1;
    action.timeScale = 1;
    action.reset().play();
    this.blockOverlay = action;
  }

  private stopBlockOverlay(): void {
    if (!this.blockOverlay) return;
    this.blockOverlay.fadeOut(CROSSFADE_SECONDS);
    this.blockOverlay = null;
  }

  private playLoop(clipName: string): void {
    // An ATTACK/PARRY overlay starting and ending would otherwise re-enter
    // this with the same locomotion clip and restart it mid-stride.
    if (clipName === this.currentBaseClip && this.currentAction?.isRunning()) return;
    this.currentBaseClip = clipName;
    const clip = this.clips.get(clipName);
    if (!clip || !this.mixer) return;
    const action = this.mixer.clipAction(clip);
    action.setLoop(THREE.LoopRepeat, Infinity);
    action.clampWhenFinished = false;
    action.timeScale = 1;
    this.crossFadeTo(action);
  }

  /**
   * Formula 3 — plays once, time-scaled so total playback exactly spans
   * targetDurationS. The attack no longer routes through here at all — a
   * swing is an additive overlay now (updateCombatOverlay), which is also
   * where Phase 2b's per-phase speed curve is armed.
   */
  private playOnceTimed(clipName: string, targetDurationS: number): void {
    const clip = this.clips.get(clipName);
    if (!clip || !this.mixer || targetDurationS <= 0) return;
    const action = this.mixer.clipAction(clip);
    action.setLoop(THREE.LoopOnce, 1);
    action.clampWhenFinished = true;
    const baseRate = clip.duration / targetDurationS;
    action.timeScale = clamp(baseRate, PLAYBACK_RATE_MIN, PLAYBACK_RATE_MAX); // Edge Case #1
    action.reset();
    this.crossFadeTo(action);
  }

  private playOnceHeld(clipName: string): void {
    const clip = this.clips.get(clipName);
    if (!clip || !this.mixer) return;
    const action = this.mixer.clipAction(clip);
    action.setLoop(THREE.LoopOnce, 1);
    action.clampWhenFinished = true;
    action.timeScale = 1;
    action.reset();
    this.crossFadeTo(action);
  }

  /** Edge Case #3 — every animation-state change cross-fades, mid-clip or not, instead of popping. */
  private crossFadeTo(next: THREE.AnimationAction): void {
    next.reset().play();
    if (this.currentAction && this.currentAction !== next) {
      next.crossFadeFrom(this.currentAction, CROSSFADE_SECONDS, false);
    }
    this.currentAction = next;
  }

  dispose(): void {
    this.mixer?.stopAllAction();
    this.disposeImpactVfx();
    this.disposeLevelUpVfx();
    this.healthBar?.dispose();
    this.root.removeFromParent();
    this.root.traverse((child) => {
      const mesh = child as THREE.Mesh;
      if (!mesh.isMesh) return;
      mesh.geometry?.dispose();
      const material = mesh.material;
      for (const m of Array.isArray(material) ? material : [material]) m?.dispose();
    });
  }
}
